

// src/pages/RequestManagement/DetailsDrawer.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
} from "@mui/material";
import StatusDropdown from "../../../components/StatusDropdown";
import CustomText, { textStyles } from "../../../components/CustomText";
import profilecross from "../../../assets/profilecross.svg";
import CustomButton from "../../../components/CustomButton";
import changeStatus from "../../../assets/StatusIcons/changeStatus.svg";
import ChangeStatusModal from "./ChangeStatusModel";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import {toast} from "react-hot-toast"
import TruncatedCell from "../../../components/TruncatedCell";
import document1 from "../../../assets/document1.svg";
import {
  convertUTCToLocalDateTime,
  formatCalendarDateForDisplay,
} from "../../../components/dateTimeUtils";

const HeaderStyles = {
  color: "#44546F",
  fontSize: "15px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 600,
};

const DataStyles = {
  color: "#172B4D",
  fontSize: "13px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
};

const RequestDetailsDrawer = ({ open, onClose, row ,onStatusChanged }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const token = useSelector((state) => state.auth.token);
  const { company } = useSelector((state) => state.auth);
  const decryptedToken = token ? decryptToken(token) : null;
  const companyId = company?.id;
  const extractAdditionalInfo = (text) => {
  if (!text) return "-";

  // Split at "Additional Information:" and get the part after it
  const parts = text.split("Additional Information:");
  if (parts.length > 1) {
    return parts[1].trim(); // remove extra newlines/spaces
  }
  return text.trim();
};
  const formatDate = (dateString) => {
    const formatted = formatCalendarDateForDisplay(dateString, { locale: "en-GB" });
    return formatted === "—" ? "-" : formatted;
  };

  /** API stores ENUM-style values; matches the label shown in the requests table. */
  const formatRequestTypeLabel = (type) => {
    if (!type) return "-";
    const keyByType = {
      LEAVE: "requestManagement.requestTypes.LEAVE",
      SCHEDULE_CHANGE: "requestManagement.requestTypes.SCHEDULE_CHANGE",
      GENERIC_HR: "requestManagement.requestTypes.GENERIC_HR",
    };
    const i18nKey = keyByType[type];
    if (i18nKey) return t(i18nKey);
    return String(type)
      .split("_")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ");
  };

  /** Leave reason code enum → translated label. */
  const formatReasonCodeLabel = (code) => {
    if (!code) return "-";
    const keyByCode = {
      URGENT: "requestManagement.leaveReasonCodes.urgent",
      SICK: "requestManagement.leaveReasonCodes.sick",
      VACATION: "requestManagement.leaveReasonCodes.vacation",
      OTHER: "requestManagement.leaveReasonCodes.others",
    };
    const i18nKey = keyByCode[code];
    return i18nKey ? t(i18nKey) : code;
  };

  const formatTime = (timeString) => {
    const formatted = convertUTCToLocalDateTime(timeString, {
      locale: "en-GB",
      formatOptions: {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
    });
    return formatted === "—" ? "-" : formatted;
  };

  const formatDays = (daysString) => {
    if (!daysString) return "-";
    // Convert "MON,TUE,WED,THU,FRI" to "Mon, Tue, Wed, Thu, Fri"
    return daysString
      .split(",")
      .map((day) => day.trim().charAt(0) + day.trim().slice(1).toLowerCase())
      .join(", ");
  };
  const fileUrl = details?.file_url?.trim?.() || "";
  const hasValidFileUrl = (() => {
    if (!fileUrl) return false;
    try {
      new URL(fileUrl);
      return true;
    } catch {
      return false;
    }
  })();

  const isPdfFile = hasValidFileUrl && fileUrl.toLowerCase().endsWith(".pdf");
  // Fetch full request details when drawer opens
  useEffect(() => {
    if (open && row?.id) {
      const fetchDetails = async () => {
        try {
          setLoading(true);
          const res = await fetch(
            `${BASE_URL}/requests/v1/companies/${companyId}/requests/${row.id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${decryptedToken}`,
              },
            }
          );
          if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
          const data = await res.json();
          setDetails(data.data);
        } catch (err) {
          console.error("Failed to fetch request details", err);
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    }
  }, [open, row, companyId, decryptedToken]);

  const InfoBox = ({ children }) => (
    <Box
      mt={2}
      sx={{
        border: "1px solid #091E4224",
        borderRadius: "10px",
        px: 2,
        py: 2,
      }}
    >
      {children}
    </Box>
  );

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 0.5, sm: 1 },
        mb: 2,
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 16,
          fontWeight: 400,
          color: "#5E5C5C",
          textAlign: "left",
          flexShrink: 0,
        }}
      >
        {label}
      </CustomText>
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 15,
          fontWeight: 400,
          color: "#172B4D",
          textAlign: { xs: "left", sm: "right" },
          wordBreak: "break-word",
          minWidth: 0,
        }}
      >
        {value || "--"}
      </CustomText>
    </Box>
  );

  if (!row) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 500 },
          maxWidth: "100vw",
          backgroundColor: "white",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <IconButton onClick={onClose} sx={{ flexShrink: 0 }}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Request #{row.id}
          </CustomText>
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 2, mt: 2 }}>
        {loading ? (
          <CustomText sx={{fontSize:"14px"}}>Loading details...</CustomText>
        ) : details ? (
          <>
            <InfoBox>
              <InfoRow
                label={t("requestDetails.status")}
                value={
                  <Box sx={{ pointerEvents: "none" }}>
                    <StatusDropdown currentStatus={details.status} />
                  </Box>
                }
              />
              {details.details && (
  <InfoRow
    label={t("requestDetails.details")}
        value={<TruncatedCell text={extractAdditionalInfo(details.details) || "-"} />}
  />
)}
              <InfoRow
                label={t("requestDetails.type")}
                value={formatRequestTypeLabel(details.type)}
              />
              
              {/* Leave Request Fields */}
              {details.type === "LEAVE" && (
                <>
                  <InfoRow
                    label={t("requestDetails.reasonCode")}
                    value={formatReasonCodeLabel(details.leave_reason_code)}
                  />
                  <InfoRow
                    label={t("requestDetails.reason")}
                    value={<TruncatedCell text={details.leave_reason_text || "-"} />}
                  />
                  <InfoRow
                    label={t("requestDetails.startDate")}
                    value={details.leave_start_date ? formatDate(details.leave_start_date) : "-"}
                  />
                  <InfoRow
                    label={t("requestDetails.endDate")}
                    value={details.leave_end_date ? formatDate(details.leave_end_date) : "-"}
                  />
                  {details.leave_partial && (
                    <>
                      <InfoRow
                        label={t("requestDetails.partialLeave") || "Partial Leave"}
                        value={details.leave_partial ? "Yes" : "No"}
                      />
                      {details.leave_partial_start && (
                        <InfoRow
                          label={t("requestDetails.partialStart") || "Partial Start Time"}
                          value={formatTime(details.leave_partial_start)}
                        />
                      )}
                      {details.leave_partial_end && (
                        <InfoRow
                          label={t("requestDetails.partialEnd") || "Partial End Time"}
                          value={formatTime(details.leave_partial_end)}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {/* Schedule Change Request Fields */}
              {details.type === "SCHEDULE_CHANGE" && (
                <>
                  <InfoRow
                    label={t("requestDetails.effectiveFrom") || "Effective From"}
                    value={details.sched_effective_from ? formatDate(details.sched_effective_from) : "-"}
                  />
                  <InfoRow
                    label={t("requestDetails.currentDays") || "Current Days"}
                    value={details.sched_current_days ? formatDays(details.sched_current_days) : "-"}
                  />
                  <InfoRow
                    label={t("requestDetails.startTime") || "Start Time"}
                    value={details.sched_current_start_time ? formatTime(details.sched_current_start_time) : "-"}
                  />
                  <InfoRow
                    label={t("requestDetails.endTime") || "End Time"}
                    value={details.sched_current_end_time ? formatTime(details.sched_current_end_time) : "-"}
                  />
                  <InfoRow
                    label={t("requestDetails.reason")}
                    value={<TruncatedCell text={details.sched_reason_text || "-"} />}
                  />
                </>
              )}

              {/* Generic/Other Request Fields */}
              {details.type !== "LEAVE" && details.type !== "SCHEDULE_CHANGE" && (
                <>
                  {details.subject && (
                    <InfoRow
                      label={t("requestDetails.subject") || "Subject"}
                      value={<TruncatedCell text={details.subject} />}
                    />
                  )}
                  {details.details && (
                    <InfoRow
                      label={t("requestDetails.details")}
                      value={<TruncatedCell text={details.details} />}
                    />
                  )}
                </>
              )}

              <InfoRow
                label={t("requestDetails.requester")}
                value={details.requester_name || "-"}
              />
              <InfoRow
                label={t("requestDetails.email")}
                value={details.requester_email || "-"}
              />
              <InfoRow
                label={t("requestDetails.date") || t("common.date") || "Date"}
                value={details.created_at ? formatDate(details.created_at) : "-"}
              />
              {details.decided_at && (
                <InfoRow
                  label={t("requestDetails.decidedAt") || "Decided At"}
                  value={formatDate(details.decided_at)}
                />
              )}
              {details.decided_by_name && (
                <InfoRow
                  label={t("requestDetails.decidedBy") || "Decided By"}
                  value={details.decided_by_name}
                />
              )}
              <InfoRow
                label={t("requestManagement.tableHeaders.comments")}
                value={<TruncatedCell text={details.admin_comment || "-"} />}
              />
            </InfoBox>

  <CustomText
      sx={{
        fontSize: 18,
        fontWeight: 500,
        color: "#2C384C",
        mb: 1,
        mt:2,
        fontFamily: "Poppins, sans-serif",
        textAlign:"left"
      }}
    >
      {t("requestDetails.requestDocument") || "Request Document"}
    </CustomText>

  <Box
    sx={{
      mt: 3,
      border: "1px solid #091E4224",
      borderRadius: "10px",
      p: 2,
    }}
  >
{!hasValidFileUrl ? (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f9f9f9",
      borderRadius: "6px",
      width: "100%",
      height: 250,
      textAlign: "center",
      px: 2,
    }}
  >
    <CustomText
      sx={{
        fontSize: 14,
        fontWeight: 500,
        color: "#5E5C5C",
      }}
    >
      {t("requestDetails.noValidDocumentUrl") || "No valid document URL found"}
    </CustomText>
  </Box>
) : isPdfFile ? (
  // 📄 Show PDF icon and name
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#f9f9f9",
      borderRadius: "6px",
      p: 2,
      width: "100%",
      height: 250,
      textAlign: "center",
    }}
  >
    <img
      src={document1}
      alt="PDF Document"
      style={{ width: 60, height: 60, marginBottom: 8 }}
    />
    <CustomText
      sx={{
        fontSize: 14,
        fontWeight: 400,
        color: "#172B4D",
        textAlign: "center",
        wordBreak: "break-all",
      }}
    >
      {details.file_name || fileUrl.split("/").pop()}
    </CustomText>
  </Box>
) : (
  // 🖼️ Image preview
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f9f9f9",
      borderRadius: "6px",
      overflow: "hidden",
      width: "100%",
      height: 250,
    }}
  >
    <img
      crossOrigin="anonymous"
      src={fileUrl}
      alt="Request Document"
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
      }}
      onError={(e) => {
        e.target.style.display = "none";
        const fallbackText = e.target.nextElementSibling;
        if (fallbackText) fallbackText.style.display = "block";
      }}
    />
    <CustomText
      sx={{
        display: "none",
        fontSize: 14,
        fontWeight: 500,
        color: "#5E5C5C",
        textAlign: "center",
        px: 2,
      }}
    >
      {t("requestDetails.noValidDocumentUrl") || "No valid document URL found"}
    </CustomText>
  </Box>
)}

{isPdfFile && (
  <Box sx={{ textAlign: "right", mt: 2 }}>
    <CustomButton
      label={t("requestDetails.viewDocument") || "View PDF"}
      size="small"
      onClick={() =>
        window.open(fileUrl, "_blank", "noopener,noreferrer")
      }
    />
  </Box>
)}
  </Box>

            <Box sx={{ mt: 3 }}>
         
{!["APPROVED", "REJECTED","CANCELLED"].includes(details.status?.toUpperCase()) && (
  <Box sx={{ mt: 3 }}>
    <CustomButton
      onClick={() => setModalOpen(true)}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={changeStatus}
            alt="save"
            style={{ width: 16, height: 16 }}
          />
          {t("requestDetails.changeStatus")}
        </Box>
      }
      size="medium"
    />
  </Box>
)}

            </Box>
          </>
        ) : (
          <CustomText>No details found.</CustomText>
        )}
      </Box>

 

 <ChangeStatusModal
  open={modalOpen}
  onClose={() => setModalOpen(false)}
  currentStatus={row.status} // 👈 pass current status here

  onSave={async (data) => {
    try {
      const res = await fetch(
        `${BASE_URL}/requests/v1/companies/${companyId}/requests/${row.id}/decision`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
          body: JSON.stringify(data),
        }
      );

      if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
      const respData = await res.json();

      toast.success("Status updated successfully");

      // ✅ update local UI instantly
      setDetails((prev) => ({
        ...prev,
        status:
          data.action === "APPROVE"
            ? "APPROVED"
            : data.action === "REJECT"
            ? "REJECTED"
            : data.action === "REQUEST_INFO"
            ? "INFO_REQUESTED"
            : prev.status,
        admin_comment: data.comment,
      }));

      if (typeof onStatusChanged === "function") {
        onStatusChanged();
      }

     setTimeout(() => {
  setModalOpen(false); // close status modal first

  // then close details drawer after 1 more second
  setTimeout(() => {
    onClose();
  }, 1000);
}, 1000);
    } catch (err) {
      console.error("Failed to update status", err);
      toast.error(err.message || "Failed to update status");

      // close modal after 1 sec even on error
      setTimeout(() => setModalOpen(false), 1000);
    }
  }}
/>




    </Drawer>
  );
};

export default RequestDetailsDrawer;



// // src/components/RequestDetailsDrawer.js
// import React,{useState} from "react";
// import {
//   Box,
//   Drawer,
//   IconButton,
//   Button,
//   Divider,
// } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
// import StatusDropdown from "../../../components/StatusDropdown";
// import CustomText, { textStyles } from "../../../components/CustomText";
// import profilecross from "../../../assets/profilecross.svg";
// import down from "../../../assets/down.svg";
// import CustomButton from "../../../components/CustomButton";
// import changeStatus from "../../../assets/StatusIcons/changeStatus.svg";
// import ChangeStatusModal from "./ChangeStatusModel";
// import { useTranslation } from "react-i18next";
// const HeaderStyles = {
//   color: "#44546F",
//   fontSize: "15px",
//   fontFamily: "Poppins, sans-serif",
//   fontWeight: 600,
// };

// const DataStyles = {
//   color: "#172B4D",
//   fontSize: "13px",
//   fontFamily: "Poppins, sans-serif",
//   fontWeight: 400,
// };

// const RequestDetailsDrawer = ({ open, onClose, row }) => {
//       const [modalOpen, setModalOpen] = useState(false);
//         const { t } = useTranslation();
//     const InfoBox = ({ children }) => (
//       <Box
//         mt={2}
//         sx={{
//           border: "1px solid #091E4224",
//           borderRadius: "10px",
//           px: 2,
//           py: 2,
//         }}
//       >
//         {children}
//       </Box>
//     );
    
//     // Reusable Row
//     const InfoRow = ({ label, value }) => (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "center",
//           mb: 2,
//         }}
//       >
//         <CustomText
//           sx={{
//             ...textStyles.h1,
//             fontSize: 16,
//             fontWeight: 400,
//             color: "#5E5C5C",
//             textAlign: "left",
//           }}
//         >
//           {label}
//         </CustomText>
//         <CustomText
//           sx={{
//             ...textStyles.h1,
//             fontSize: 15,
//             fontWeight: 400,
//             color: "#172B4D",
//             textAlign: "right",
//           }}
//         >
//           {value || "--"}
//         </CustomText>
//       </Box>
//     );
//   if (!row) return null;

//   return (
//     <Drawer
//       anchor="right"
//       open={open}
//       onClose={onClose}
//       PaperProps={{
//         sx: {
//           width: 500,
//           backgroundColor: "white",
//         },
//       }}
//     >
     
//          <Box
//               sx={{
//                 position: "sticky",
//                 top: 0,
//                 zIndex: 10,
//                 backgroundColor: "#E7EBEE",
//                 p: 2,
//                 py: 1,
//                 borderBottom: "1px solid #eee",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "space-between",
//               }}
//             >
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <IconButton onClick={onClose}>
//                   <img src={profilecross} alt="close" />
//                 </IconButton>
//                 <CustomText
//                   sx={{
//                     fontSize: 20,
//                     fontWeight: 600,
//                     color: "#2C384C",
//                     fontFamily: "Poppins, sans-serif",
//                   }}
//                 >
//                      {row.id}
//                 </CustomText>
//               </Box>
//             </Box>

//       <Box sx={{ px: 2, pb: 2 ,mt:2, }}>
//         <Box
//           sx={{
         
//           }}
//         >

//                  <InfoBox>
    
//             <InfoRow
//  label={t("requestDetails.status")}
//     value={
//       <Box sx={{ pointerEvents: "none" }}>
//         <StatusDropdown currentStatus={row.status} />
//       </Box>
//     }
//   />
//            <InfoRow
//    label={t("requestDetails.requested")}

//     value=
//      {row.requestedDateLong || row.date}
    
//   />
//           <InfoRow         label={t("requestDetails.reasonCode")}
//           value={row.reasonCode || "Urgent"}/>
//           <InfoRow        label={t("requestDetails.requestType")} value={row.absenceWindow || "Leave"}/>
//                  <InfoRow       label={t("requestDetails.absenceRequest")}value={row.absent || "9:00 AM - 2:00 PM"}/>
//           <InfoRow  label={t("requestDetails.reason")} value={row.fullReason || row.reason}/>
      
//         </InfoBox>
    

//              <Box sx={{ mt: 3 }}>
//                  <CustomButton
//                  onClick={() => setModalOpen(true)}
//                    label={
//                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
               
//                        <img src={changeStatus} alt="save" style={{ width: 16, height: 16 }} />
//                            {t("requestDetails.changeStatus")}
//                      </Box>
//                    }
//                    size="medium"
//                  />
//                </Box>
//         </Box>

//       </Box>
//             <ChangeStatusModal
//         open={modalOpen}
//         onClose={() => setModalOpen(false)}
//         onSave={(data) => {
//           console.log("Status updated:", data);
//         }}
//       />
//     </Drawer>
//   );
// };

// export default RequestDetailsDrawer;
